import { PathModeKey, QueryType } from '../../utils/apollo/graphqlSchema';
import { useBootstrapSelector } from '../../redux/selectorHooks';
import { CHART_TYPES } from './constants/chartTypes';
import LineChart from './LineChart';
import ChartLegend from './ChartLegend';
import ChartDataTable from './ChartDataTable';
import BarChart from './BarChart';
import FunnelChart from './FunnelChart';
import ComparativeLineChart from './ComparativeLineChart';
import StackedBarChart from './StackedBarChart';
import AreaChart from './AreaChart';
import MetricChart from './MetricChart';
import SankeyChart from './SankeyChart';
import TopPathsChart from './TopPathsChart';
import RetentionChart from './RetentionChart';
import { RetentionBarChart, RetentionLineChart } from './RetentionSubChart';
import PredictionChart from './PredictionChart';
import { CohortSampleChart } from './CohortSampleChart';
import SignificanceChart from './SignificanceChart';
import GeoMap from './GeoMap';
import SinglePieChart from './SinglePieChart';
import ChartAnalysisTable from './ChartAnalysisTable';
import { ChartMode } from './types';
import ResponsiveChartLoader from './ChartLoader/ResponsiveChartLoader';
import { UnreachableCaseError } from 'ts-essentials';
import { SingleBarChart } from './SingleDateChart';
import SingleFunnelChart from './SingleFunnelChart/SingleFunnelChart';
import { useChartContext } from './ChartContext';

interface IProps {
  analysisId: string;
  mode: ChartMode;
}

const ChartView = ({ analysisId, mode }: IProps) => {
  const geoMapData = useBootstrapSelector('geoMapData');
  const {
    settings: { chartSettings },
    chartData,
  } = useChartContext();
  const { chartType, singleDayLabel } = chartSettings;

  const showLegend = mode !== ChartMode.Compact;

  switch (chartType) {
    case CHART_TYPES.LINE_CHART:
      return (
        <>
          <LineChart analysisId={analysisId} mode={mode} />
          {showLegend && <ChartLegend analysisId={analysisId} />}
        </>
      );
    case CHART_TYPES.DATA_TABLE:
      return <ChartDataTable analysisId={analysisId} mode={mode} />;
    case CHART_TYPES.BAR_CHART:
      return (
        <>
          {/* Either expanded on selected day or histogram with all time */}
          {singleDayLabel ? (
            <SingleBarChart analysisId={analysisId} mode={mode} />
          ) : (
            <BarChart analysisId={analysisId} mode={mode} />
          )}
          {showLegend && <ChartLegend analysisId={analysisId} />}
        </>
      );
    case CHART_TYPES.FUNNEL_CHART:
      return (
        <>
          {singleDayLabel ? (
            <SingleFunnelChart analysisId={analysisId} mode={mode} />
          ) : (
            <FunnelChart analysisId={analysisId} mode={mode} />
          )}
          {showLegend && <ChartLegend analysisId={analysisId} />}
        </>
      );
    case CHART_TYPES.COMPARATIVE_LINE_CHART:
      return (
        <>
          <ComparativeLineChart analysisId={analysisId} mode={mode} />
          {showLegend && <ChartLegend analysisId={analysisId} />}
        </>
      );
    case CHART_TYPES.STACKED_COLUMN_CHART:
      return (
        <>
          <StackedBarChart analysisId={analysisId} mode={mode} />
          {showLegend && <ChartLegend analysisId={analysisId} />}
        </>
      );
    case CHART_TYPES.PERCENT_STACKED_AREA_CHART:
      return (
        <>
          <AreaChart analysisId={analysisId} mode={mode} />
          {showLegend && <ChartLegend analysisId={analysisId} />}
        </>
      );
    case CHART_TYPES.METRIC_CHART:
      return (
        <>
          <MetricChart analysisId={analysisId} />
          {showLegend && <ChartLegend analysisId={analysisId} />}
        </>
      );
    case CHART_TYPES.PATH:
      if (chartData.queryType === QueryType.Path) {
        switch (chartData.mode) {
          case PathModeKey.Sankey:
            return (
              <>
                <SankeyChart analysisId={analysisId} mode={mode} />
                {showLegend && <ChartLegend analysisId={analysisId} />}
              </>
            );
          case PathModeKey.TopPaths:
            return <TopPathsChart analysisId={analysisId} mode={mode} />;
          default:
            throw new UnreachableCaseError(chartData);
        }
      } else {
        throw new Error(
          'Attempt to render path chart type with non-Path chart data',
        );
      }
    case CHART_TYPES.RETENTION:
      return (
        <>
          <RetentionChart analysisId={analysisId} />
          {showLegend && <ChartLegend analysisId={analysisId} />}
        </>
      );
    case CHART_TYPES.RETENTION_LINE:
      return (
        <>
          <RetentionLineChart analysisId={analysisId} mode={mode} />
          {showLegend && <ChartLegend analysisId={analysisId} />}
        </>
      );
    case CHART_TYPES.RETENTION_BAR:
      return (
        <>
          <RetentionBarChart analysisId={analysisId} mode={mode} />
          {showLegend && <ChartLegend analysisId={analysisId} />}
        </>
      );
    case CHART_TYPES.PREDICTION:
      return <PredictionChart analysisId={analysisId} />;
    case CHART_TYPES.COHORT_SAMPLE:
      return <CohortSampleChart analysisId={analysisId} mode={mode} />;
    case CHART_TYPES.SIGNIFICANCE:
      return <SignificanceChart analysisId={analysisId} mode={mode} />;
    case CHART_TYPES.GEO_MAP:
      if (!geoMapData) {
        return <ResponsiveChartLoader mode={mode} />;
      }
      return (
        <>
          <GeoMap analysisId={analysisId} />
          {showLegend && <ChartLegend analysisId={analysisId} />}
        </>
      );
    case CHART_TYPES.PIE_CHART:
      return (
        <>
          <SinglePieChart analysisId={analysisId} mode={mode} />
          {showLegend && <ChartLegend analysisId={analysisId} />}
        </>
      );
    case CHART_TYPES.EXPRESSION_TABLE:
      return <ChartAnalysisTable analysisId={analysisId} mode={mode} />;
    default:
      throw new UnreachableCaseError(chartType);
  }
};

export default ChartView;
